import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { 
  ServiceSelection
} from '../../types/planningTypes';
import { updateSongSelections } from '../../../../services/planningService';
import { 
  resequenceSelections, 
  createNewSelection, 
  prepareUpdateRequest,
  validateSelections,
  validateSequence 
} from '../utils/selectionUtils';

interface UseSelectionsFormProps {
  initialSelections: ServiceSelection[];
  serviceId: number;
  onUpdateComplete: (selections: ServiceSelection[]) => void;
}

export const useSelectionsForm = ({ initialSelections, serviceId, onUpdateComplete }: UseSelectionsFormProps) => {
 
  const [currentSelections, setCurrentSelections] = useState<ServiceSelection[]>(
    resequenceSelections(initialSelections)
  );
  const [originalSelections] = useState<ServiceSelection[]>(initialSelections);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSelectionChange = useCallback((updatedSelections: ServiceSelection[]) => {
    const resequenced = resequenceSelections(updatedSelections);
    setCurrentSelections(resequenced);
  }, []);

  const handleAddSelection = useCallback(() => {
    setCurrentSelections(prev => {
      const newSelection = createNewSelection(serviceId, prev);
      return resequenceSelections([...prev, newSelection]);
    });
  }, [serviceId]);

  const handleDeleteSelection = useCallback((index: number) => {
    setCurrentSelections(prev => {
      const newSelections = prev.filter((_, i) => i !== index);
      return resequenceSelections(newSelections);
    });
  }, []);

  const handleReorder = useCallback((fromIndex: number, toIndex: number) => {
    setCurrentSelections(prev => {
      const newSelections = [...prev];
      const [removed] = newSelections.splice(fromIndex, 1);
      newSelections.splice(toIndex, 0, removed);
      return resequenceSelections(newSelections);
    });
  }, []);

  const handleAppendSelections = useCallback((newSelections: ServiceSelection[]) => {
    setCurrentSelections(prev => resequenceSelections([...prev, ...newSelections]));
  }, []);

  const handleUpdate = useCallback(async () => {
    if (isSubmitting) return;

    if (!validateSelections(currentSelections)) {
      toast.error('Please complete all required fields for each selection');
      return;
    }
  
    if (!validateSequence(currentSelections)) {
      toast.error('There was an error with the song order. Please try again.');
      setCurrentSelections(resequenceSelections([...originalSelections]));
      return;
    }
  
    setIsSubmitting(true);
    try {
      const updateRequest = prepareUpdateRequest(originalSelections, currentSelections);
      
      if (Object.keys(updateRequest).length === 0) {
        toast.info('No changes to update');
        return currentSelections;
      }

      const result = await updateSongSelections(updateRequest);
      
      if (result.updated || result.added || result.deleted) {
        toast.success('Song selections updated successfully');
        // Ensure we're passing back the current selections
        onUpdateComplete(currentSelections);
        return currentSelections;
      } else {
        throw new Error('Update returned unexpected structure');
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update selections');
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [currentSelections, originalSelections, isSubmitting, onUpdateComplete]);

  return {
    currentSelections,
    isSubmitting,
    handleSelectionChange,
    handleAddSelection,
    handleDeleteSelection,
    handleReorder,
    handleAppendSelections,
    handleUpdate
  };
};